import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Mail, MessageCircle, Check, X } from 'lucide-react';

export const Contact = () => {
  const handleWhatsAppClick = () => {
    // Using the official WhatsApp API format with proper encoding
    const phoneNumber = '60165264131';
    const message = encodeURIComponent('Hi, I am interested in discussing a growth opportunity.');
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#F5F5F5]">
      <section className="container mx-auto px-6 py-20 max-w-5xl">
        <div className="text-center space-y-8 mb-16">
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            I fix breakdowns in paid acquisition systems.
          </h1>
          <p className="text-xl text-[#888888] max-w-3xl mx-auto leading-relaxed">
            When ad spend is scaling but revenue has stalled, the problem isn't your ads or creative—it's structural. I diagnose where the acquisition system is failing and rebuild the performance loop.
          </p>
        </div>

        {/* ICP Block */}
        <Card className="bg-[#1a1a1a] border-none p-8 mb-16">
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl font-bold text-[#F5F5F5] mb-4">Ideal Customer Profile</h2>
              <p className="text-lg text-[#F5F5F5] leading-relaxed">
                I work with performance-led brands where paid acquisition is already a meaningful revenue driver, but growth has become inefficient or unstable as spend scales.
              </p>
            </div>

            {/* Typical Characteristics */}
            <div>
              <h3 className="text-xl font-semibold text-[#F5F5F5] mb-4">Typically:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>$20k+ monthly paid media spend (Meta / TikTok / Google)</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>Paid acquisition is a core revenue channel, not experimental</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>ROAS / CAC has started to degrade or fluctuate</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>Creative fatigue is present (ads stop scaling consistently)</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>Funnel conversion issues exist between click → purchase</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <Check className="w-5 h-5 text-[#E85D04] mt-0.5 flex-shrink-0" />
                  <span>Strong product-market fit, but weak acquisition system structure</span>
                </li>
              </ul>
            </div>

            {/* Best Fit Industries */}
            <div>
              <h3 className="text-xl font-semibold text-[#F5F5F5] mb-4">Best fit industries:</h3>
              <div className="flex flex-wrap gap-3">
                {['DTC E-commerce', 'Performance SaaS', 'High-consideration purchases', 'Regulated verticals (iGaming, fintech)'].map((industry) => (
                  <span key={industry} className="bg-[#0a0a0a] text-[#E85D04] px-4 py-2 rounded-lg text-sm border border-[#E85D04]/30">
                    {industry}
                  </span>
                ))}
              </div>
            </div>

            {/* Not a Fit */}
            <div className="border-t border-[#888888]/20 pt-6">
              <h3 className="text-xl font-semibold text-[#F5F5F5] mb-4">Not a fit:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-[#888888]">
                  <X className="w-5 h-5 text-[#888888] mt-0.5 flex-shrink-0" />
                  <span>Brands testing paid acquisition for the first time</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <X className="w-5 h-5 text-[#888888] mt-0.5 flex-shrink-0" />
                  <span>Monthly ad spend below $15k</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <X className="w-5 h-5 text-[#888888] mt-0.5 flex-shrink-0" />
                  <span>Brands looking for general marketing strategy or brand building</span>
                </li>
                <li className="flex items-start gap-3 text-[#888888]">
                  <X className="w-5 h-5 text-[#888888] mt-0.5 flex-shrink-0" />
                  <span>Product-market fit is unclear or unproven</span>
                </li>
              </ul>
            </div>

            {/* Closing Line */}
            <div className="border-t border-[#888888]/20 pt-6">
              <p className="text-[#888888] italic">
                I work with a small number of clients at a time. If the above describes your situation, reach out below.
              </p>
            </div>
          </div>
        </Card>

        <div className="text-center space-y-8">
          {/* Contact Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/80 text-white border-2 border-[#E85D04] px-8 py-6 text-lg font-semibold transition-colors w-full sm:w-auto"
              onClick={() => window.location.href = 'mailto:bryan.cyh2088@gmail.com'}
            >
              <Mail className="mr-2 w-5 h-5" />
              Send me an email
            </Button>
            <Button 
              className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/80 text-white border-2 border-[#25D366] px-8 py-6 text-lg font-semibold transition-colors w-full sm:w-auto"
              onClick={handleWhatsAppClick}
            >
              <MessageCircle className="mr-2 w-5 h-5" />
              Message on WhatsApp
            </Button>
          </div>

          {/* Contact Info */}
          <div className="text-[#888888] text-sm pt-8">
            <p>bryan.cyh2088@gmail.com · Malaysia, Remote · +60165264131</p>
          </div>
        </div>
      </section>
    </div>
  );
};